import { useMutation } from "@apollo/client";
import { useState } from "react";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import TextField from "@/components/TextField";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import { POST_M360_FEEDBACK_MUTATION } from "./mutations";

const FEEDBACK_HEADLINE = "Product Support";
const FEEDBACK_BODY =
  "Ask a question or tell us what you'd change! Your comments will be sent straight to the Member 360 product team.";

type M360FeedbackDialogProps = {
  open: boolean;
  onClosed: () => void;
};

export default function M360FeedbackDialog({ open, onClosed }: M360FeedbackDialogProps) {
  const [message, setMessage] = useState("");
  const trimmedMessage = message.trim();
  const [postFeedback, { loading }] = useMutation(POST_M360_FEEDBACK_MUTATION, {
    onError() {
      showErrorSnackbar("Message not sent", { subtitle: "Try again later." });
    }
  });

  const closeDialog = () => {
    if (loading) return;
    setMessage("");
    onClosed();
  };

  const sendMessage = async () => {
    if (!trimmedMessage || loading) return;

    try {
      const response = await postFeedback({
        variables: {
          message: trimmedMessage,
          pageUrl: window.location.href
        }
      });
      const result = response.data?.postM360Feedback;
      if (result?.success) {
        showSnackbar("Message sent");
        setMessage("");
        onClosed();
        return;
      }
      showErrorSnackbar("Message not sent", { subtitle: result?.message || "Try again later." });
    } catch {
      // onError already surfaces a snackbar
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={closeDialog}
      headline={FEEDBACK_HEADLINE}
      className="w-[560px] max-w-[95vw] max-h-[85vh]"
      hasFormElements
      disableClose={loading}
      actions={
        <>
          <Button variant="text" onClick={closeDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={() => {
              void sendMessage();
            }}
            disabled={!trimmedMessage}
            loading={loading}
            testId="m360-feedback-send"
          >
            Send Message
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="m-0 text-body-medium text-sys-brand-on-surface-variant">{FEEDBACK_BODY}</p>
        <TextField
          label="Message"
          type="textarea"
          rows={6}
          className="w-full"
          value={message}
          disabled={loading}
          onChangeText={setMessage}
        />
      </div>
    </Dialog>
  );
}
